/**
 * Hook for processing asset attributes with port/protocol matching
 *
 * Wraps pure attribute processing functions with React useMemo for performance
 */

import { useMemo } from 'react';

import { TechnologyAttributeUnion } from '@/sections/asset/components/tabs';
import { processAssetAttributes } from '@/sections/asset/utils/assetAttributeProcessing';
import { processTechnologies } from '@/sections/asset/utils/overview/technologyProcessing';
import { Attribute, TreeNode } from '@/types';

export interface UseAssetAttributeProcessingResult {
  /** All attributes grouped by name */
  attributes: Record<string, Attribute[]>;
  /** Technique attributes (ports matched with protocols) */
  techniqueAttributes: Record<string, Attribute>;
  /** Combined technologies with attributes */
  technologies: TechnologyAttributeUnion[];
}

/**
 * Custom hook for processing asset attributes
 *
 * @param assetAttributes - Array of asset attributes to process
 * @param technologiesTree - Tree of technology relationships from graph query
 * @returns Processed attributes, technique attributes, and technologies
 */
export function useAssetAttributeProcessing(
  assetAttributes: Attribute[],
  technologiesTree: TreeNode[]
): UseAssetAttributeProcessingResult {
  // Process attributes to extract ports, protocols, and groupings
  const { attributes, techniqueAttributes } = useMemo(
    () => processAssetAttributes(assetAttributes),
    [assetAttributes.length]
  );

  // Combine technologies tree with technique attributes
  const technologies = useMemo(
    () => processTechnologies(technologiesTree, techniqueAttributes),
    [technologiesTree.length, Object.keys(techniqueAttributes).length]
  );

  return {
    attributes,
    techniqueAttributes,
    technologies,
  };
}
